import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material'
import * as Sentry from '@sentry/react'

import { useStudentContext } from 'common/context/StudentContext'
import Video from 'student/common/video'
import { PaperContainer } from 'student/common/taskUI'
import { studentSignout } from 'common/utils/signout'

import AwakeningLogo from 'assets/student/awakening-logo.png'
import AwakeningImage from 'assets/student/student-awakening.png'
import AwakeningImageMobile from 'assets/student/student-awakening-mobile.png'

const VIDEOS = {
  0: '0002',
  1: '0004',
  2: '0006',
}

const VIDEO_CATEGORY = 'numGoodbye'

function AwakeningGoodbye({ loading, handleContinue, handleSignout }) {
  return (
    <Container>
      <PaperContainer
        sx={{
          height: { xs: '70vh', md: '100%' },
          px: 2,
          backgroundImage: {
            xs: `url('${AwakeningImageMobile}')`,
            sm: `url('${AwakeningImage}')`,
          },
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'bottom',
          backgroundSize: 'contain',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box textAlign='center'>
          <Typography
            variant='h4'
            sx={{
              fontWeight: 600,
              mt: 3,
              display: { xs: 'block', sm: 'none' },
            }}
          >
            XtraMath Session Complete!
          </Typography>
          <Typography
            variant='h2'
            sx={{
              fontWeight: 600,
              mt: 3,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            XtraMath Session Complete!
          </Typography>
          <Typography
            variant='subtitle1'
            sx={{
              fontSize: { xs: '1rem', md: '1.5rem' },
              mt: 2,
              maxWidth: { xs: '100%', md: '70%' },
              mx: 'auto',
            }}
          >
            Great job on your Xtramath today! Your math is getting better each
            time. Now you can join Mr.C in Awakening.
          </Typography>
        </Box>
        <Box
          sx={{
            flex: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            flexDirection: 'column',
          }}
        >
          <Box
            component='img'
            src={AwakeningLogo}
            sx={{ mb: 3, display: { xs: 'none', sm: 'block' } }}
          />
          <Button
            variant='contained'
            color='secondary'
            size='large'
            onClick={handleContinue}
            disabled={loading}
          >
            {loading ? <CircularProgress size='1rem' /> : 'Play Awakening Now'}
          </Button>
        </Box>
      </PaperContainer>
      <Box textAlign='center'>
        <Button
          variant='contained'
          color='success'
          size='large'
          sx={{ mt: 3 }}
          onClick={handleSignout}
          aria-label='Sign out'
        >
          Sign Out
        </Button>
      </Box>
    </Container>
  )
}

function Goodbye() {
  const { studentSession } = useStudentContext()
  const [video, setVideo] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const useAwakening = studentSession?.student?.useAwakening?.enabled

  useEffect(() => {
    if (!studentSession) return
    const videoSrc = VIDEOS[studentSession.student.getVideo(VIDEO_CATEGORY)]
    setVideo(videoSrc)
  }, [studentSession])

  const handleLaunchAwakening = () => {
    setLoading(true)
    Sentry.addBreadcrumb({
      category: 'breadcrumb',
      message: `Clicked on Launch Awakening in Goodbye`,
      level: 'info',
      data: {
        student: studentSession?.student,
        activity: studentSession.currentState()?.activity,
      },
    })

    if (video) studentSession.student.watchedVideo(VIDEO_CATEGORY)
    studentSession
      ?.screenFinished('launch_awakening')
      .then((response: any) => {
        if (response.joinUrl) window.location.href = response.joinUrl
      })
      .catch((error) => {
        console.error('Error launching Awakening:', error)
        setLoading(false)
      })
  }

  const handleSignout = () => {
    Sentry.addBreadcrumb({
      category: 'breadcrumb',
      message: 'Clicked on sign out in Goodbye',
      level: 'info',
      data: {
        student: studentSession?.student,
        activity: studentSession.currentState()?.activity,
      },
    })

    if (video) studentSession.student.watchedVideo(VIDEO_CATEGORY)
    studentSession
      ?.screenFinished('goodbye')
      .then(() => studentSignout(studentSession))
  }

  if (useAwakening)
    return (
      <AwakeningGoodbye
        loading={loading}
        handleContinue={handleLaunchAwakening}
        handleSignout={handleSignout}
      />
    )

  return (
    <Container>
      <PaperContainer sx={{ height: '100%', px: 2 }}>
        <Box textAlign='center'>
          <Typography variant='h2' sx={{ fontWeight: 600, mt: 3 }}>
            XtraMath Session Complete!
          </Typography>
          <Typography
            variant='subtitle1'
            sx={{
              fontSize: { xs: '1rem', md: '1.5rem' },
              mt: 2,
              maxWidth: { xs: '100%', md: '70%' },
              mx: 'auto',
            }}
          >
            Great job on your XtraMath today! Your math is getting better each
            time. Remember to sign out before you go. See you next time!
          </Typography>
          <Box
            sx={{
              position: { xs: 'relative', md: 'absolute' },
              bottom: '0',
              width: '100%',
              textAlign: 'center',
              mt: { xs: 3, md: 0 },
            }}
            mb={-1}
          >
            <Video
              video={video}
              sx={{
                width: { xs: '100%', md: '40%' },
              }}
            />
          </Box>
        </Box>
      </PaperContainer>
      <Box textAlign='center'>
        <Button
          variant='contained'
          color='secondary'
          size='large'
          sx={{ mt: 3 }}
          onClick={handleSignout}
          aria-label='Sign out'
        >
          Sign Out
        </Button>
      </Box>
    </Container>
  )
}

export default Goodbye
